import { FC, useState, memo } from 'react';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import FileUtils from 'application/utils/FileUtils';
import { toast } from '@shared/ui';
import AnalyticsService, {
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
} from 'application/services/AnalyticsService';

import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';
import IconChevronDown from '@zvuk-b2b/react-uikit/ui/icons/ChevronDown';
import IconChevronRight from '@zvuk-b2b/react-uikit/ui/icons/ChevronRight';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import IconButton from '@zvuk-b2b/react-uikit/ui/IconButton';
import IconCopy from '@zvuk-b2b/react-uikit/ui/icons/Copy';
import IconDownload from '@zvuk-b2b/react-uikit/ui/icons/Download';

import { TEXTS } from './texts';
import { useOnboardingContext } from '../../useOnboardingContext';
import { useOnboardingActionContext } from '../../useOnboardingActionContext';
import { FLOWS_FOR_NOT_SHOW_STOP_ONBOARDING_WARNING } from '../../consts';

import styles from './DownloadDesktopModal.module.scss';

const appLink = 'https://ftp.muzlab.ru/updates/desktop-player/v2/latest/windows-i386/muzlab-desktop-client-3.0.2.exe';

const DownloadDesktopModal: FC<ModalProps[ModalTypes.DESKTOP_PLAYER_DOWNLOAD]> = ({
  openModal,
  replaceModal,
  close,
}) => {
  const { flowType } = useOnboardingContext();
  const { clearCurrentPoint } = useOnboardingActionContext();

  const [isRequirementsExtanded, setIsRequirementsExtanded] = useState(false);
  const { isMobile } = useMedia();

  const onClickCopy = () => {
    navigator.clipboard.writeText(appLink);

    toast.info(TEXTS.LINK_WAS_COPIED);
  };

  const onDownloadClick = () => {
    if (!isMobile) {
      FileUtils.downloadExternalFile(appLink);
      toast.info(TEXTS.APP_WAS_DOWNLOADED);
    } else {
      toast.info(TEXTS.APP_WAS_NOT_DOWNLOADED);
    }

    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_DESKTOP_DOWNLOAD_CLICKED, {
      [StreamBindFlowParamNameAMP.FROM_MOBILE]: isMobile,
    });
  };

  const onClickNext = () => {
    replaceModal(ModalTypes.DESKTOP_PLAYER_BIND, {});
  };

  const handleCancel = () => {
    if (flowType && FLOWS_FOR_NOT_SHOW_STOP_ONBOARDING_WARNING.has(flowType)) {
      close();
      clearCurrentPoint();
      return;
    }

    openModal(ModalTypes.STREAM_STOP_BINDING_FLOW_WARNING, {});
  };

  return (
    <ModalScreen
      titlePlacement="left"
      onClose={handleCancel}
      className={styles.modalContainer}
      contentClassName={styles.modalContent}
      title={TEXTS.MODAL_TITLE}
      actionButtons={[
        <Button
          key="button_desktop_player_modal_next"
          elementId="button_desktop_player_modal_next"
          type={ButtonType.PRIMARY}
          onClick={onClickNext}>
          {TEXTS.NEXT_BUTTON}
        </Button>,
        <Button
          key="button_desktop_player_modal_download_cancel"
          elementId="button_desktop_player_modal_download_cancel"
          type={ButtonType.SECONDARY}
          onClick={() => {
            replaceModal(ModalTypes.SELECT_PLAYER_MODAL, {});
          }}>
          {TEXTS.CANCEL_BUTTON}
        </Button>,
      ]}>
      <Button
        type={ButtonType.PLAIN}
        className={styles.expandButton}
        onClick={() => {
          setIsRequirementsExtanded(!isRequirementsExtanded);
        }}
        elementId="button_desktop_player_modal_expand_button">
        <span>{TEXTS.INSTALLATION_GUIDE_EXPAND_BUTTON}</span>

        <div className={styles.expandButtonIcon}>
          {isRequirementsExtanded ? <IconChevronDown /> : <IconChevronRight />}
        </div>
      </Button>

      {isRequirementsExtanded && (
        <ul data-testid="desktop_player_modal_requirements_list">
          <li>{TEXTS.REQUIREMENTS_ITEM_ONE}</li>
          <li>{TEXTS.REQUIREMENTS_ITEM_TWO}</li>
          <li>{TEXTS.REQUIREMENTS_ITEM_THREE}</li>
          <li>{TEXTS.REQUIREMENTS_ITEM_FOUR}</li>
          <li>{TEXTS.REQUIREMENTS_ITEM_FIVE}</li>
          <li>{TEXTS.REQUIREMENTS_ITEM_SIX}</li>
        </ul>
      )}

      {isMobile && (
        <Button
          type={ButtonType.PLAIN}
          className={styles.copyButton}
          onClick={onClickCopy}
          elementId="button_desktop_player_modal_copy_link">
          <div className={styles.copyButtonIcon}>
            <IconCopy />
          </div>
          <span>{TEXTS.COPY_APP_LINK}</span>
        </Button>
      )}

      {!isMobile && (
        <div className={styles.container}>
          <Button
            key="button_desktop_player_modal_download_success"
            elementId="button_desktop_player_modal_download_success"
            type={ButtonType.PLAIN}
            className={styles.copyButton}
            onClick={onDownloadClick}>
            <div className={styles.copyButtonIcon}>
              <IconDownload />
            </div>

            <span>{TEXTS.SUCCESS_BUTTON}</span>
          </Button>

          <IconButton
            key="button_desktop_player_modal_copy_link"
            elementId="button_desktop_player_modal_copy_link"
            onClick={onClickCopy}>
            <IconCopy className={styles.iconButton} />
          </IconButton>
        </div>
      )}
    </ModalScreen>
  );
};

export default memo(DownloadDesktopModal);
